"use client";

import { talentNetworkText } from "@/i18n/talentNetwork.constants";
import messages from "@/i18n/messages/en.json";

import { BottomSheet } from "@artha/ui";

import { ResumePersonalizationSheet } from "./ResumePersonalizationSheet";

import { CandidateProfileCards } from "./profile-cards/CandidateProfileCards";

import {
  PERSONALIZATION_SHEET_CLASS_NAME,
  PERSONALIZATION_SHEET_CONTENT_CLASS_NAME,
  PERSONALIZATION_SHEET_OVERLAY_CLASS_NAME,
} from "../personalization.constants";

import {
  clearCandidateProfileDraft,
  saveCandidateProfileDraft,
} from "../services/profileDraft";

import { CandidateContactVerificationStep } from "./verification/CandidateContactVerificationStep";
import { CONTACT_VERIFICATION_TITLE } from "./verification/verification.constants";
import type { CandidateProfileFormProps } from "../personalization.types";

import { useCandidateProfileForm } from "../hooks/useCandidateProfileForm";
export function CandidateProfileForm({
  username,
  creatorName,
  source,
  controller,
  draft,
  session,
}: CandidateProfileFormProps) {
  const {
    router,
    t,
    isTalentNetwork,
    verificationOpen,
    setVerificationOpen,
    uploadOpen,
    setUploadOpen,
    signInOpen,
    setSignInOpen,
    requestIdentitySignIn,
    completeSignIn,
    profile,
    setProfile,
    setOwnerExternalId,
    externalId,
    setExternalId,
    setSavedProfile,
    saving,
    editingContact,
    setEditingContact,
    deliveryState,
    setDeliveryState,
    saveError,
    setSaveError,
    hasChanges,
    phoneVerified,
    emailVerified,
    needsVerification,
    openToOpportunities,
    completeVerification,
    rememberProfile,
    rememberSavedProfile,
    submitProfile,
  } = useCandidateProfileForm({
    username,
    creatorName,
    source,
    controller,
    draft,
    session,
  });

  return (
    <>
      {saveError && (
        <p role="alert" className="mb-4 text-sm text-[#B42318]">
          {saveError}
        </p>
      )}
      <CandidateProfileCards
        profile={profile}
        disabled={verificationOpen || signInOpen || saving}
        submitting={saving}
        submitDisabled={isTalentNetwork && !needsVerification && !hasChanges}
        emailVerified={emailVerified}
        phoneVerified={phoneVerified}
        editingContact={editingContact}
        onContactEditorClose={() => setEditingContact(null)}
        onReupload={() => setUploadOpen(true)}
        onChange={(next) => {
          setProfile(next);
          rememberProfile(next);
          setSaveError(null);
        }}
        onSubmit={submitProfile}
        submitLabel={
          needsVerification
            ? talentNetworkText("continue_to_verification")
            : isTalentNetwork || hasChanges
              ? talentNetworkText("save_changes")
              : talentNetworkText("find_relevant_jobs")
        }
      />
      {!isTalentNetwork && controller.isAuthenticated ? (
        <button
          type="button"
          className="mt-4 w-full text-sm font-semibold text-[#9D1C1C]"
          disabled={controller.isLoggingOut}
          onClick={async () => {
            try {
              await controller.logout();
              clearCandidateProfileDraft(username, source);
              router.replace(`/@${encodeURIComponent(username)}`);
            } catch {
              /* The controller exposes logout errors. */
            }
          }}
        >
          {controller.isLoggingOut
            ? t("actions.loggingOut")
            : t("actions.logout")}
        </button>
      ) : null}
      {controller.error && (
        <p role="alert" className="mt-4 text-sm text-[#B42318]">
          {controller.error}
        </p>
      )}
      <BottomSheet
        isOpen={verificationOpen}
        onClose={() => setVerificationOpen(false)}
        title={CONTACT_VERIFICATION_TITLE}
        desktopPlacement="bottom"
        className={PERSONALIZATION_SHEET_CLASS_NAME}
        contentClassName={PERSONALIZATION_SHEET_CONTENT_CLASS_NAME}
        overlayClassName={PERSONALIZATION_SHEET_OVERLAY_CLASS_NAME}
      >
        {verificationOpen && (
          <CandidateContactVerificationStep
            username={username}
            source={source}
            creatorId={controller.creatorId}
            externalId={externalId}
            posthogDistinctId={controller.posthogDistinctId || externalId}
            onPrepared={rememberSavedProfile}
            deliveryState={deliveryState}
            onDeliverySent={(contact, delivery) =>
              setDeliveryState((current) => ({
                ...current,
                [contact]: delivery,
              }))
            }
            onEditContact={(contact) => {
              setVerificationOpen(false);
              setEditingContact(contact);
            }}
            profile={profile}
            openToOpportunities={openToOpportunities}
            onComplete={completeVerification}
            onIdentityConflict={requestIdentitySignIn}
          />
        )}
      </BottomSheet>
      <ResumePersonalizationSheet
        isOpen={uploadOpen || signInOpen}
        onClose={() => {
          setUploadOpen(false);
          setSignInOpen(false);
        }}
        controller={controller}
        initialStep={signInOpen ? "sign_in" : "upload"}
        signInMessage={signInOpen
          ? isTalentNetwork
            ? messages.common.candidatePhoneIdentityConflict
            : messages.common.candidateIdentityConflict
          : undefined}
        mode={isTalentNetwork ? "talent_network" : "personalization"}
        creatorName={isTalentNetwork ? creatorName : undefined}
        creatorUsername={username}
        onComplete={completeSignIn}
        onParsed={(result) => {
          if (!result.editableProfile) return;
          const updated = {
            externalId: result.externalId,
            ownerExternalId: result.ownerExternalId,
            editableProfile: result.editableProfile,
            ...(isTalentNetwork && { openToOpportunities }),
          };
          controller.restoreProfileDraft(updated);
          setExternalId(result.externalId);
          setOwnerExternalId(result.ownerExternalId);
          setProfile(result.editableProfile);
          setSavedProfile(undefined);
          setSaveError(null);
          saveCandidateProfileDraft(username, source, updated);
          setUploadOpen(false);
          setSignInOpen(false);
        }}
      />
    </>
  );
}
